import React, { useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import PropTypes from 'prop-types';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import { useTranslation } from '@/hooks/useTranslation';
import { MESSAGE_WIDTH, MESSAGE_WIDTH_AMOUNTS } from '@/constants/config';

const DAY_MS = 24 * 60 * 60 * 1000;

function startOfLocalDay(timestamp) {
  const d = new Date(timestamp);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

const createStyles = (messageWidth) => StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginVertical: 10,
    paddingHorizontal: 16,
    alignSelf: 'center',
    width: '100%',
    maxWidth:
      messageWidth === MESSAGE_WIDTH.NARROW ? MESSAGE_WIDTH_AMOUNTS.NARROW :
      messageWidth === MESSAGE_WIDTH.MEDIUM ? MESSAGE_WIDTH_AMOUNTS.MEDIUM : '100%',
  },
  line: {
    flex: 1,
    height: StyleSheet.hairlineWidth,
  },
  label: {
    marginHorizontal: 10,
  },
});

function MessageDateSeparator({ timestamp, messageWidth }) {
  const { colors } = useTheme();
  const { t } = useTranslation();

  const styles = useMemo(() => createStyles(messageWidth), [messageWidth]);

  const label = useMemo(() => {
    if (!timestamp) return '';
    const day = startOfLocalDay(timestamp);
    const today = startOfLocalDay(Date.now());
    const diffDays = Math.round((today - day) / DAY_MS);
    if (diffDays === 0) return t('common.today') || 'Today';
    if (diffDays === 1) return t('common.yesterday') || 'Yesterday';
    const d = new Date(day);
    return d.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      ...(d.getFullYear() !== new Date().getFullYear() ? { year: 'numeric' } : {}),
    });
  }, [timestamp, t]);

  if (!label) return null;

  return (
    <View style={styles.container} accessibilityRole="text" accessibilityLabel={label}>
      <View style={[styles.line, { backgroundColor: colors.border_color_secondary }]} />
      <Text size="xs" color="tertiary" style={styles.label}>
        {label}
      </Text>
      <View style={[styles.line, { backgroundColor: colors.border_color_secondary }]} />
    </View>
  );
}

MessageDateSeparator.propTypes = {
  timestamp: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  messageWidth: PropTypes.string,
};

MessageDateSeparator.displayName = 'MessageDateSeparator';

export default React.memo(MessageDateSeparator);
